import React from "react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import SearchableSelect from "../../ui/SearchableSelect";
import { MultiTechSelect } from "./MultiTechSelect";
import { TechSplitEditor } from "./TechSplitEditor";
import { Briefcase, MessageSquare } from "lucide-react";

const JobDetailsSection = ({
  formData, setFormData, loading,
  jobSources, jobTypes, teamMembers,
  handleSourceSelect, handleServiceSelect, handleTechnicianSelect,
  sendAssignmentSMS, setSendAssignmentSMS,
  isSmsReady, hasActivePhone, editingJob,
}) => {
  // Map our backend rows to the {value,label,hint?} shape SearchableSelect
  // expects. Hint shows the duration next to service names so 10+ services
  // with similar names are still distinguishable in the search results.
  const sourceOptions = jobSources.map((s) => ({
    value: s.source_id,
    label: s.name,
  }));
  const serviceOptions = jobTypes
    .filter((jt) => jt.is_active !== false)
    .map((jt) => ({
      value: jt.service_id,
      label: jt.name,
      hint: jt.default_duration ? `${jt.default_duration}h` : undefined,
    }));

  // Preview of the auto-generated title (service + vehicle) shown as the
  // placeholder so users know what they get if they leave the field empty.
  const _svc = jobTypes.find((jt) => jt.service_id === formData.service_id);
  const _vehicle = [formData.vehicle_year, formData.vehicle_make, formData.vehicle_model].filter(Boolean).join(" ");
  const autoTitle = _svc
    ? (_vehicle ? `${_svc.name} - ${_vehicle}` : _svc.name)
    : "Auto-generated from service type";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[#FFCC00]">
        <Briefcase className="w-4 h-4" />
        <span className="text-sm font-medium">Job Details</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        {/* Job Source — searchable when ≥10 entries */}
        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-xs sm:text-sm">Job Source</Label>
          <SearchableSelect
            value={formData.source_id}
            onChange={(v) => handleSourceSelect(v === "" ? " " : v)}
            options={sourceOptions}
            placeholder={loading ? "Loading..." : (jobSources.length === 0 ? "No sources" : "Select source")}
            allowEmpty
            emptyLabel="-- None --"
            disabled={loading}
            triggerClassName="input-glass"
            data-testid="job-source-select"
          />
        </div>

        {/* Service Type — searchable when ≥10 entries */}
        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-xs sm:text-sm">Service Type</Label>
          <SearchableSelect
            value={formData.service_id}
            onChange={(v) => handleServiceSelect(v === "" ? " " : v)}
            options={serviceOptions}
            placeholder={loading ? "Loading..." : (jobTypes.length === 0 ? "No services" : "Select service")}
            allowEmpty
            emptyLabel="-- None --"
            disabled={loading}
            triggerClassName="input-glass"
            data-testid="job-service-select"
          />
        </div>

        {/* Technician */}
        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-xs sm:text-sm">Assign Technician</Label>
          <Select value={formData.assigned_to} onValueChange={handleTechnicianSelect}>
            <SelectTrigger className="input-glass" data-testid="job-technician-select">
              <SelectValue placeholder={loading ? "Loading..." : "Unassigned"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">Unassigned</SelectItem>
              {teamMembers.filter(m => {
                if (m.status !== "active") return m.member_id === formData.assigned_to;
                if (formData.service_id && formData.service_id.trim()) {
                  return !m.services || m.services.length === 0 || m.services.includes(formData.service_id);
                }
                return true;
              }).map((member) => (
                <SelectItem key={member.member_id} value={member.member_id}>
                  {member.name}{member.status !== "active" ? " (Disabled)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Optional custom job title — blank keeps the auto-generated one */}
        <div className="col-span-full space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-xs sm:text-sm">
            Job Title <span className="text-muted-foreground/50">(optional)</span>
          </Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder={autoTitle}
            maxLength={120}
            className="input-glass"
            data-testid="job-title-field"
          />
        </div>

        {/* Additional Technicians (dropdown multi-select, equal commission split) */}
        {formData.assigned_to && formData.assigned_to.trim() && (
          <div className="space-y-1.5 sm:space-y-2" data-testid="additional-techs-section">
            <Label className="text-muted-foreground text-xs sm:text-sm">Additional Technicians</Label>
            <MultiTechSelect
              options={teamMembers
                .filter(m => m.status === "active" && m.member_id !== formData.assigned_to)
                .map(m => ({ id: m.member_id, name: m.name }))}
              selected={formData.additional_technician_ids || []}
              onChange={(ids) => setFormData(prev => ({ ...prev, additional_technician_ids: ids }))}
              placeholder="None"
              triggerClassName="input-glass"
              testIdPrefix="additional-tech"
            />
            {(formData.additional_technician_ids || []).length > 0 && (
              <TechSplitEditor
                techs={[
                  { id: formData.assigned_to, name: teamMembers.find(m => m.member_id === formData.assigned_to)?.name || "Primary Tech", primary: true },
                  ...(formData.additional_technician_ids || []).map(id => ({
                    id, name: teamMembers.find(m => m.member_id === id)?.name || "Technician",
                  })),
                ]}
                mode={formData.tech_commission_split_mode || "equal"}
                unit={formData.tech_commission_split_unit || "percent"}
                percents={formData.tech_commission_splits_map || {}}
                amounts={formData.tech_commission_splits_amount_map || {}}
                onChange={({ mode, unit, percents, amounts }) => setFormData(prev => ({
                  ...prev, tech_commission_split_mode: mode, tech_commission_split_unit: unit,
                  tech_commission_splits_map: percents, tech_commission_splits_amount_map: amounts,
                }))}
                testIdPrefix="tech-split"
              />
            )}
          </div>
        )}

        {/* Send Assignment SMS Checkbox */}
        {formData.assigned_to && formData.assigned_to.trim() && !editingJob && (
          <div className={`col-span-full flex items-center gap-2 p-3 rounded-lg border ${hasActivePhone === false ? "bg-red-500/10 border-red-500/20" : "bg-[#FFCC00]/10 border-[#FFCC00]/20"}`}>
            <input
              type="checkbox"
              id="sendAssignmentSMS"
              checked={sendAssignmentSMS}
              onChange={(e) => {
                if (e.target.checked && !isSmsReady()) return;
                setSendAssignmentSMS(e.target.checked);
              }}
              disabled={hasActivePhone === false}
              className="w-4 h-4 rounded border-white/20 bg-theme-surface text-[#FFCC00] focus:ring-[#FFCC00]"
            />
            <label htmlFor="sendAssignmentSMS" className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <MessageSquare className="w-4 h-4 text-[#FFCC00]" />
              Send assignment SMS to technician
            </label>
            {hasActivePhone === false && (
              <span className="ml-auto text-xs text-red-400">No phone number configured</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailsSection;
